<template>
  <widget-temp :widgetTitle="$t('charts.inventory_vn_title')" widgetName="InventoryVNChart" v-bind:widgetId="6">
    <template slot="widget-content">
      <div class="chart-container">
        <div v-if="!chartData || chartData.length === 0" class="empty-state">
          <v-icon large color="grey lighten-2">mdi-warehouse</v-icon>
          <div class="empty-text">{{ $t('charts.no_data_vn') }}</div>
        </div>
        <highcharts v-else :options="chartOptions"></highcharts>
      </div>
    </template>
  </widget-temp>
</template>

<script>
import { mapState } from "vuex";

export default {
  name: "inventory-vn-chart",
  data() {
    return {};
  },
  computed: {
    ...mapState({
      loading: state => state.dashboard.loading,
      chartData: state => state.dashboard.inventory_vn_chart
    }),
    chartHeight() {
      const tqLength = (this.$store.state.dashboard.inventory_tq_chart || []).length;
      const vnLength = (this.$store.state.dashboard.inventory_vn_chart || []).length;
      const maxLength = Math.max(tqLength, vnLength);
      return Math.max(300, maxLength * 28 + 80);
    },
    chartOptions() {
      const self = this;
      const data = (this.chartData || []).map((item, index) => {
        // Generate a gradient of colors from teal/green (high stock) to lime
        const hue = 150 + (index / Math.max((this.chartData.length - 1), 1)) * 30;
        return {
          name: item.customer_name || 'N/A',
          y: Number(item.remaining_packs) || 0,
          color: `hsl(${hue}, 70%, 45%)`,
          totalWeight: Number(item.total_weight) || 0,
          productCount: Number(item.product_count) || 0,
        };
      });

      const categories = data.map(d => d.name);

      return {
        chart: {
          type: 'bar',
          height: this.chartHeight,
          style: {
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif'
          },
          marginLeft: 130,
        },
        title: { text: '' },
        xAxis: {
          categories: categories,
          crosshair: true,
          labels: {
            style: {
              color: '#374151',
              fontSize: '11px',
              fontWeight: '500'
            },
            formatter: function() {
              const label = this.value;
              return label.length > 14 ? label.substring(0, 14) + '…' : label;
            }
          },
          lineColor: '#e5e7eb',
          tickColor: '#e5e7eb',
        },
        yAxis: {
          min: 0,
          title: {
            text: this.$t('charts.inventory_axis_y'),
            style: { color: '#6b7280', fontSize: '11px', fontWeight: '500' }
          },
          labels: {
            style: { color: '#9ca3af', fontSize: '10px' }
          },
          gridLineColor: '#f3f4f6',
          gridLineDashStyle: 'Dash'
        },
        tooltip: {
          backgroundColor: '#ffffff',
          borderWidth: 1,
          borderColor: '#e5e7eb',
          borderRadius: 8,
          shadow: { color: 'rgba(0,0,0,0.08)', offsetX: 0, offsetY: 4, opacity: 1, width: 12 },
          useHTML: true,
          formatter: function() {
            const unitKien = self.$t('charts.unit_kien');
            const unitVnInventory = self.$t('charts.vn_inventory');
            const unitMaHang = self.$t('charts.unit_ma_hang');
            return `<div style="padding:4px 2px">
              <span style="font-size:11px;color:#6b7280;font-weight:600">${this.point.name}</span><br/>
              <span style="color:${this.point.color}">●</span>
              <b style="color:#111827"> ${this.y} ${unitKien} ${unitVnInventory}</b><br/>
              <span style="color:#9ca3af;font-size:10px">
                ${this.point.productCount} ${unitMaHang} · ${(this.point.totalWeight || 0).toFixed(1)} kg
              </span>
            </div>`;
          }
        },
        plotOptions: {
          bar: {
            pointPadding: 0.15,
            groupPadding: 0.05,
            borderWidth: 0,
            borderRadius: 4,
            colorByPoint: true,
            dataLabels: {
              enabled: true,
              format: '{point.y}',
              style: {
                fontSize: '11px',
                fontFamily: 'inherit',
                fontWeight: '600',
                color: '#374151',
                textOutline: 'none'
              },
              align: 'left',
              inside: false,
              x: 4
            }
          }
        },
        series: [{
          name: this.$t('charts.series_remaining_packs'),
          data: data
        }],
        legend: { enabled: false },
        credits: { enabled: false }
      };
    }
  },
  watch: {
    chartData: {
      handler() {
        this.$nextTick(() => {
          this.$emit('rendered');
        });
      },
      immediate: true
    }
  },
  methods: {
    getChartData() {
      this.$store.dispatch("dashboard/getInventoryVNChart");
    }
  },
  mounted() {
    this.getChartData();
  }
};
</script>

<style scoped>
.chart-container {
  padding: 16px 20px 20px;
  min-height: 300px;
}
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 260px;
  gap: 12px;
}
.empty-text {
  font-size: 13px;
  color: #9ca3af;
}
</style>
